/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * workOrderTimerView.js
 *
 * Pure model of the service-module (work sheet) timer. It turns what the SERVER
 * knows into the `{status, totalElapsed, breakElapsed}` objects the work sheet
 * screen renders — and does so WITHOUT accumulating anything on the device.
 *
 * WHY THIS EXISTS: the work sheet used to count with a local `setInterval`
 * (`totalElapsed + 1000` per tick). Android suspends JS timers while the screen
 * is locked, so every locked minute was silently lost. The workday and the
 * project timer never had that bug because the backend owns their clock: it
 * stamps `startedAt`, the app only shows `now - startedAt`. This module gives the
 * work sheet the same shape:
 *
 *   elapsed(scope) = SETTLED seconds the backend already closed for that scope
 *                  + LIVE seconds of the one open segment (`now - startedAt`).
 *
 * Both inputs come from the server — `timers[]` of the work order (closed entries
 * carry `durationSeconds`) and the widget-facade timer (contract C1:
 * `startedAt`/`state`/`taskId`) — and the only thing read from the device is the
 * wall clock, which keeps running while the phone is locked. There is no counter
 * to lose.
 *
 * No React, network or native imports: unit-testable.
 *
 * @module helpers/workOrderTimerView
 */

import {resolveTrackerElapsedSeconds, resolveTrackerLabels, resolveTrackerState} from './timeTrackerFormat';
import {isServerRecordId} from './serviceRecordId';

/** Timer states with an open segment (a live clock). */
export const LIVE_TIMER_STATES = ['running', 'break'];

/** Prefix of the synthetic key the screen uses for the whole-work-order timer. */
export const WORK_ORDER_TIMER_PREFIX = 'wo-';

/**
 * Key of the whole-work-order timer (separate namespace from task ids).
 * @param {string} [workOrderId] Work order `_id` (or its sheet number while unsaved).
 * @returns {string} Timer key, e.g. `wo-65f0…`.
 */
export const workOrderTimerKey = (workOrderId) => `${WORK_ORDER_TIMER_PREFIX}${workOrderId || 'new'}`;

/**
 * Task scope behind a timer key.
 * @param {string} timerKey `workOrderTimerKey(...)` or a task `_id`.
 * @returns {string|null} The task id, or `null` for the whole-work-order timer.
 */
export const taskIdOfTimerKey = (timerKey) => {
    const key = String(timerKey ?? '');

    return !key || key.startsWith(WORK_ORDER_TIMER_PREFIX) ? null : key;
};

/**
 * Normalise an id-ish value (string, ObjectId-like, populated ref) to a string.
 * @param {*} value Raw id.
 * @returns {string} The id, `''` when absent.
 */
const idOf = (value) => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'object') {
        return String(value._id ?? value.id ?? '');
    }

    return String(value);
};

/**
 * Whole non-negative seconds between a server timestamp and `nowMs`. Clamped at 0
 * so a device clock that runs slightly behind the server never shows a negative
 * time.
 * @param {string|number|Date|null} startedAt Server timestamp the open segment started at.
 * @param {number} [nowMs] Device clock (injectable for tests).
 * @returns {number} Whole seconds.
 */
export const liveSecondsSince = (startedAt, nowMs = Date.now()) => {
    if (!startedAt) {
        return 0;
    }

    const started = new Date(startedAt).getTime();

    return Number.isNaN(started) ? 0 : Math.max(0, Math.floor((nowMs - started) / 1000));
};

const NOTHING_SETTLED = Object.freeze({workSeconds: 0, breakSeconds: 0});

/**
 * Seconds the backend has already CLOSED, bucketed per scope for one technician.
 *
 * `timers[]` holds tracked and hand-entered entries alike; an entry without an
 * `endTime` is the open segment (counted live, never here). Entries are bucketed
 * by their own `taskId` — the `''` bucket is the whole-work-order scope. Computed
 * once per fresh server read and reused by every 1 s render, so a long-lived work
 * order with thousands of imported entries is not re-summed each second.
 *
 * @param {object[]} entries `workOrder.timers[]` as the API returns it.
 * @param {string} userId Technician the numbers are for (empty = unknown = nothing counted).
 * @returns {Map<string, {workSeconds: number, breakSeconds: number}>} Closed seconds per scope.
 */
export const bucketSettledSeconds = (entries, userId) => {
    const me = idOf(userId);
    const buckets = new Map();

    // Until the technician is known nothing is "theirs": counting every colleague's closed seconds next to
    // THEIR live segment would show a wrong total for the first frames (or for good if the profile is missing).
    if (!me) {
        return buckets;
    }

    (Array.isArray(entries) ? entries : []).forEach((entry) => {
        if (!entry || !entry.endTime) {
            return;
        }
        if (idOf(entry.userId) !== me) {
            return;
        }

        const scope = idOf(entry.taskId);
        const seconds = Math.max(0, Math.floor(Number(entry.durationSeconds) || 0));
        const bucket = buckets.get(scope) || {workSeconds: 0, breakSeconds: 0};

        if (entry.type === 'break') {
            bucket.breakSeconds += seconds;
        } else if (entry.type === 'work') {
            bucket.workSeconds += seconds;
        }
        buckets.set(scope, bucket);
    });

    return buckets;
};

/**
 * Seconds the backend has already CLOSED for one scope of one technician.
 * @param {object[]} entries `workOrder.timers[]` as the API returns it.
 * @param {string} userId Technician the numbers are for.
 * @param {string|null} [taskId] Task scope, `null` for the whole work order.
 * @returns {{workSeconds: number, breakSeconds: number}} Closed seconds.
 */
export const settledSecondsForScope = (entries, userId, taskId = null) => (
    bucketSettledSeconds(entries, userId).get(idOf(taskId)) || NOTHING_SETTLED
);

/**
 * Does the server timer (contract C1) run on this work order?
 * @param {object|null} timer Facade timer.
 * @param {string} workOrderId Work order `_id`.
 * @returns {boolean} True for a work-order timer on exactly this work order.
 */
export const timerBelongsToWorkOrder = (timer, workOrderId) => (
    !!timer && timer.kind === 'workOrder' && !!workOrderId && idOf(timer.id) === idOf(workOrderId)
);

/**
 * Scope the server timer should be shown on, as a task id (or `null` = the whole
 * work order). A timer started on a task this screen does not know — typically a
 * task that was re-keyed by the last save — is surfaced on the whole-work-order
 * card, so a running clock can ALWAYS be stopped from the sheet instead of ticking
 * unseen.
 *
 * @param {object|null} timer Facade timer on this work order.
 * @param {object[]} [tasks] `worksheet.tasks`.
 * @returns {string|null} Task id or `null`.
 */
export const resolveActiveScope = (timer, tasks = []) => {
    const taskId = idOf(timer?.taskId);

    if (!taskId) {
        return null;
    }

    const known = (Array.isArray(tasks) ? tasks : []).some((task) => (
        idOf(task?._id) === taskId || idOf(task?.serviceId) === taskId
    ));

    return known ? taskId : null;
};

/**
 * The segment a verb just closed, worked out from the timer before and after it.
 *
 * Normally the server's answer to "what did that verb close?" is read back from the work order. When that
 * re-read fails the closed seconds would vanish from the clock (a Stop at 10:00 would show 0:00); this keeps
 * the total continuous until the next successful read replaces it with the server's exact figure.
 *
 * @param {object} input Inputs.
 * @param {object|null} input.before The caller's timer before the verb.
 * @param {object|null} input.after The caller's timer after the verb.
 * @param {string} input.workOrderId Work order `_id`.
 * @param {number} [input.nowMs] Device clock.
 * @returns {{taskId: string, type: ('work'|'break'), seconds: number}|null} The closed segment, or null when none closed.
 */
export const closedSegmentBetween = ({before, after, workOrderId, nowMs = Date.now()}) => {
    const state = resolveTrackerState(before);

    if (!timerBelongsToWorkOrder(before, workOrderId) || !LIVE_TIMER_STATES.includes(state) || !before.startedAt) {
        return null;
    }

    const sameOpenSegment = !!after
        && idOf(after.id) === idOf(before.id)
        && resolveTrackerState(after) === state
        && String(after.startedAt || '') === String(before.startedAt);

    if (sameOpenSegment) {
        return null;
    }

    return {
        taskId: idOf(before.taskId),
        type: state === 'break' ? 'break' : 'work',
        seconds: liveSecondsSince(before.startedAt, nowMs),
    };
};

/**
 * Add locally-known closed segments on top of the server's settled buckets (see {@link closedSegmentBetween}).
 * @param {Map} buckets {@link bucketSettledSeconds} result (never mutated).
 * @param {{taskId: string, type: string, seconds: number}[]} [pending] Segments closed since the last good read.
 * @returns {Map} The same buckets when there is nothing pending, else a new Map.
 */
export const addPendingSettled = (buckets, pending = []) => {
    if (!Array.isArray(pending) || !pending.length) {
        return buckets;
    }

    const merged = new Map();

    buckets.forEach((value, key) => merged.set(key, {...value}));
    pending.forEach(({taskId, type, seconds}) => {
        const bucket = merged.get(taskId) || {workSeconds: 0, breakSeconds: 0};

        if (type === 'break') {
            bucket.breakSeconds += seconds;
        } else {
            bucket.workSeconds += seconds;
        }
        merged.set(taskId, bucket);
    });

    return merged;
};

/**
 * Adopt the identity the server gave a work sheet (and its tasks) when it was saved.
 *
 * A sheet or task the technician created on the phone carries a client-minted id until the server has stored it
 * (the screen keeps showing that id after Save). The backend books timer segments against ids it knows, so
 * before this runs a fresh task cannot be timed. Tasks are re-keyed by POSITION — a PATCH stores the array in
 * order — and only when the names line up, so a server that dropped or reordered a row can never hand one
 * task another task's time.
 *
 * @param {object} worksheet The screen's current worksheet.
 * @param {object} fresh `normalizeWorkOrder(...)` of the saved record.
 * @returns {object} New worksheet; the input when there is nothing to adopt.
 */
export const adoptServerIdentity = (worksheet, fresh) => {
    if (!fresh || !isServerRecordId(fresh._id)) {
        return worksheet;
    }

    const serverTasks = Array.isArray(fresh.tasks) ? fresh.tasks : [];
    const localTasks = Array.isArray(worksheet.tasks) ? worksheet.tasks : [];
    const sameShape = serverTasks.length === localTasks.length;
    const sameName = (a, b) => String(a?.name || '').trim() === String(b?.name || '').trim();
    let changed = !isServerRecordId(worksheet._id);

    const tasks = localTasks.map((task, index) => {
        const serverTask = serverTasks[index];

        if (sameShape && !isServerRecordId(task?._id) && isServerRecordId(serverTask?._id) && sameName(task, serverTask)) {
            changed = true;

            return {...task, _id: serverTask._id};
        }

        return task;
    });

    return changed ? {...worksheet, _id: fresh._id, tasks} : worksheet;
};

/**
 * What a Start on one scope of this work sheet has to do, given what the SERVER runs right now.
 *
 * The backend allows one open clock per technician. Opening a second segment next to a running one would bill
 * labour hours twice (and the facade only closes the SAME scope), so the previous clock is settled first:
 *
 *   - nothing live                          -> just start;
 *   - live on exactly this scope            -> `noop` (a double tap must not churn the segment);
 *   - live on another scope of this sheet   -> settle it, then start (no question — it is the same job);
 *   - live on another sheet / a project     -> `confirm` the switch, then settle, then start.
 *
 * A PAUSED clock is closed server-side already and is simply superseded by the start.
 *
 * @param {object} input Inputs.
 * @param {object|null} input.timer The caller's facade timer, freshly read.
 * @param {string} input.workOrderId Work order `_id`.
 * @param {string|null} [input.taskId] Scope to start (`null` = whole work order).
 * @param {object[]} [input.tasks] `worksheet.tasks`.
 * @returns {{noop: boolean, settle: boolean, confirm: boolean}} The plan.
 */
export const planStart = ({timer, workOrderId, taskId = null, tasks = []}) => {
    const live = LIVE_TIMER_STATES.includes(resolveTrackerState(timer));

    if (!live) {
        return {noop: false, settle: false, confirm: false};
    }

    const sameOrder = timerBelongsToWorkOrder(timer, workOrderId);

    if (sameOrder && resolveActiveScope(timer, tasks) === (idOf(taskId) || null)) {
        return {noop: true, settle: false, confirm: false};
    }

    return {noop: false, settle: true, confirm: !sameOrder};
};

/**
 * The timer the screen renders for one scope.
 *
 * @param {object} input Inputs.
 * @param {object|null} input.timer Facade timer (contract C1) of the caller, or null when idle.
 * @param {string} input.workOrderId Work order `_id`.
 * @param {string|null} [input.taskId] Scope (`null` = whole work order).
 * @param {object[]} [input.tasks] `worksheet.tasks` (see {@link resolveActiveScope}).
 * @param {object[]} [input.entries] `worksheet.timers`.
 * @param {Map} [input.settledBuckets] Pre-computed {@link bucketSettledSeconds} (skips re-summing `entries`).
 * @param {string} [input.userId] Technician.
 * @param {number} [input.nowMs] Device clock.
 * @returns {{status: ('idle'|'running'|'paused'|'break'), totalElapsed: number, breakElapsed: number, startedAt: (string|null)}}
 *   Elapsed values are MILLISECONDS (what `formatElapsed` expects).
 */
export const buildScopeTimer = ({timer, workOrderId, taskId = null, tasks = [], entries = [], settledBuckets = null, userId = '', nowMs = Date.now()}) => {
    const settled = (settledBuckets || bucketSettledSeconds(entries, userId)).get(idOf(taskId)) || NOTHING_SETTLED;
    const active = timerBelongsToWorkOrder(timer, workOrderId)
        && resolveActiveScope(timer, tasks) === (idOf(taskId) || null);
    const status = active ? resolveTrackerState(timer) : 'idle';
    let workSeconds = settled.workSeconds;
    let breakSeconds = settled.breakSeconds;

    // A paused row is already CLOSED server-side (pause settles exactly like a stop),
    // so its seconds are inside `settled`; only an open segment adds live time.
    if (status === 'running') {
        workSeconds += liveSecondsSince(timer.startedAt, nowMs);
    } else if (status === 'break') {
        breakSeconds += liveSecondsSince(timer.startedAt, nowMs);
    }

    return {
        status,
        totalElapsed: workSeconds * 1000,
        breakElapsed: breakSeconds * 1000,
        startedAt: status === 'running' || status === 'break' ? (timer.startedAt || null) : null,
    };
};

/**
 * Task id → server-tracked milliseconds, taken from a fresh work-order read.
 * @param {object[]} [tasks] `workOrder.tasks`.
 * @returns {Map<string, number>} Map keyed by `_id` AND `serviceId`.
 */
const trackedMsByTask = (tasks) => {
    const tracked = new Map();

    (Array.isArray(tasks) ? tasks : []).forEach((task) => {
        const ms = Math.max(0, Number(task?.trackedTimeMs) || 0);

        [idOf(task?._id), idOf(task?.serviceId)].filter(Boolean).forEach((key) => tracked.set(key, ms));
    });

    return tracked;
};

/**
 * Fold a fresh server read of a work order into the sheet the screen holds,
 * touching ONLY the time fields. The technician's unsaved edits (names, prices,
 * notes, …) stay exactly as typed; the time numbers are replaced by the server's.
 *
 * @param {object} worksheet The screen's current worksheet.
 * @param {object} fresh `normalizeWorkOrder(...)` of the fresh read.
 * @returns {object} New worksheet (the input is never mutated).
 */
export const mergeServerTime = (worksheet, fresh) => {
    if (!fresh) {
        return worksheet;
    }

    const trackedByTask = trackedMsByTask(fresh.tasks);

    return {
        ...worksheet,
        timers: Array.isArray(fresh.timers) ? fresh.timers : (worksheet.timers || []),
        recordedWorkSeconds: Number(fresh.recordedWorkSeconds) || 0,
        recordedBreakSeconds: Number(fresh.recordedBreakSeconds) || 0,
        trackedTechnicians: Array.isArray(fresh.trackedTechnicians) ? fresh.trackedTechnicians : (worksheet.trackedTechnicians || []),
        tasks: (Array.isArray(worksheet.tasks) ? worksheet.tasks : []).map((task) => {
            const key = [idOf(task?._id), idOf(task?.serviceId)].find((candidate) => candidate && trackedByTask.has(candidate));

            return key ? {...task, trackedTimeMs: trackedByTask.get(key)} : task;
        }),
    };
};

/** Id the dashboard card carries, so its controls can tell it from a device-local timer. */
export const SERVER_TIMER_ID = 'server-timer';

/**
 * Card model of a backend timer, in the shape the dashboard's `ActiveTimerCard` renders.
 *
 * @param {object|null} timer Facade timer (contract C1).
 * @param {number} nowMs Device clock.
 * @param {function(string, object=): string} t i18n translator.
 * @returns {object|null} `{taskId, taskName, worksheetId, status, totalElapsed, breakElapsed, startedAt}` or null when idle.
 */
export const buildServerTimerCard = (timer, nowMs, t) => {
    if (!timer || !timer.id) {
        return null;
    }

    const labels = resolveTrackerLabels(timer);
    const isOrder = timer.kind === 'workOrder';
    // A work-order clock without a task is "the work sheet's clock"; the sheet number is its context line.
    const taskName = isOrder && !timer.taskTitle
        ? t('repair_wo_timer', {defaultValue: 'Munkalap időmérő'})
        : (labels.headline || labels.eyebrow);

    return {
        taskId: SERVER_TIMER_ID,
        taskName,
        worksheetId: isOrder ? (timer.title || labels.eyebrow) : (labels.eyebrow || timer.projectTitle || ''),
        status: resolveTrackerState(timer),
        totalElapsed: resolveTrackerElapsedSeconds(timer, nowMs) * 1000,
        breakElapsed: 0,
        // A project clock has no tracked break (the facade answers 409 `break_not_supported`).
        canBreak: timer.canBreak !== false,
        startedAt: timer.startedAt ? new Date(timer.startedAt).getTime() : 0,
    };
};

export default {
    WORK_ORDER_TIMER_PREFIX,
    workOrderTimerKey,
    taskIdOfTimerKey,
    liveSecondsSince,
    bucketSettledSeconds,
    settledSecondsForScope,
    timerBelongsToWorkOrder,
    resolveActiveScope,
    buildScopeTimer,
    mergeServerTime,
    adoptServerIdentity,
    closedSegmentBetween,
    addPendingSettled,
    planStart,
    LIVE_TIMER_STATES,
    SERVER_TIMER_ID,
    buildServerTimerCard,
};
